import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { toast } from 'sonner';
import {
  Calendar, CalendarDays, ChevronDown, Edit2, Save, X, Rocket,
  TrendingUp, TrendingDown, DollarSign, Target, Loader2, CheckCircle2, CircleCheck,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import {
  backendUrl, formatCurrency, formatCompactCurrency, formatDate, getStageLabel,
} from './gameplanUtils';

// Projections View - Shows deals projected to settle in next 3 months
const ProjectionsView = ({ projectionsData, onUpdateDeal, authToken }) => {
  const [editingDeal, setEditingDeal] = useState(null);
  const [editDate, setEditDate] = useState('');
  const [settlingDeal, setSettlingDeal] = useState(null);
  const [settledDate, setSettledDate] = useState('');
  const [savingSettled, setSavingSettled] = useState(false);
  const [monthOffset, setMonthOffset] = useState(0); // 0 = current 3 months, 3 = next 3 months, etc.
  const [localProjections, setLocalProjections] = useState(null);
  const [loading, setLoading] = useState(false);
  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
  const headers = { 'Authorization': `Bearer ${authToken}` };

  // Fetch projections for the selected month range
  const fetchProjections = useCallback(async () => {
    if (!authToken) return;
    setLoading(true);
    try {
      const response = await fetch(`${backendUrl}/api/gameplan/projections?offset=${monthOffset}`, { headers });
      if (response.ok) {
        const data = await response.json();
        setLocalProjections(data);
      }
    } catch (err) {
      console.error('Error fetching projections:', err);
    } finally {
      setLoading(false);
    }
  }, [monthOffset, backendUrl, authToken]);

  useEffect(() => {
    if (monthOffset === 0 && projectionsData) {
      setLocalProjections(projectionsData);
    } else {
      fetchProjections();
    }
  }, [monthOffset, projectionsData, fetchProjections]);

  const months = localProjections?.months || [];
  const summary = localProjections?.summary || {};

  const getStatusBadge = (status) => {
    const styles = {
      'submitted': 'bg-blue-100 text-blue-700 border-blue-200',
      'approved': 'bg-emerald-100 text-emerald-700 border-emerald-200',
    };
    return styles[status] || 'bg-slate-100 text-slate-700';
  };

  const monthGradients = [
    'from-blue-500 to-indigo-600',
    'from-emerald-500 to-teal-600',
    'from-purple-500 to-pink-600'
  ];

  const handleUpdateProjectedDate = async () => {
    if (!editingDeal || !editDate) return;
    
    try {
      const response = await fetch(`${backendUrl}/api/gameplan/deals/${editingDeal.id}`, {
        method: 'PATCH',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ projected_settlement_date: editDate })
      });
      if (response.ok) {
        toast.success('Projected settlement date updated');
        setEditingDeal(null);
        setEditDate('');
        onUpdateDeal();
        fetchProjections(); // Refresh projections
      } else {
        toast.error('Failed to update');
      }
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  const openEditDate = (deal) => {
    setEditingDeal(deal);
    setEditDate(deal.projected_settlement_date || '');
  };

  const openMarkSettled = (deal) => {
    const today = new Date().toISOString().slice(0, 10);
    const proj = deal.projected_settlement_date;
    const initial = proj && proj <= today ? proj : today;
    setSettlingDeal(deal);
    setSettledDate(initial);
  };

  const handleMarkSettled = async () => {
    if (!settlingDeal || !settledDate) return;
    setSavingSettled(true);
    try {
      const response = await fetch(`${backendUrl}/api/gameplan/deals/${settlingDeal.id}`, {
        method: 'PATCH',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          status_stage: 'settled',
          settled_date: settledDate,
        }),
      });
      if (response.ok) {
        toast.success(`${settlingDeal.client_name} marked as settled`);
        setSettlingDeal(null);
        setSettledDate('');
        onUpdateDeal && onUpdateDeal();
        fetchProjections();
      } else {
        toast.error('Failed to mark as settled');
      }
    } catch (e) {
      toast.error('Failed to mark as settled');
    } finally {
      setSavingSettled(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Navigation Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Settlement Projections</h2>
          <p className="text-sm text-slate-500">
            {monthOffset === 0 ? 'Showing next 3 months' : `Showing ${monthOffset} - ${monthOffset + 3} months ahead`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setMonthOffset(Math.max(0, monthOffset - 3))}
            disabled={monthOffset === 0 || loading}
            className="flex items-center gap-1"
          >
            <ChevronDown className="w-4 h-4 rotate-90" />
            Previous
          </Button>
          <div className="px-3 py-1 bg-slate-100 rounded-lg text-sm font-medium text-slate-700">
            {months[0]?.short_name} - {months[2]?.short_name}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setMonthOffset(monthOffset + 3)}
            disabled={loading}
            className="flex items-center gap-1"
          >
            Next
            <ChevronDown className="w-4 h-4 -rotate-90" />
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      ) : (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white border-0">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-white/20">
                <Rocket className="w-5 h-5" />
              </div>
              <div>
                <p className="text-2xl font-bold">{formatCurrency(summary.total_projected_amount)}</p>
                <p className="text-sm text-white/80">Total Projected</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-emerald-500 to-teal-600 text-white border-0">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-white/20">
                <Target className="w-5 h-5" />
              </div>
              <div>
                <p className="text-2xl font-bold">{summary.total_deal_count || 0}</p>
                <p className="text-sm text-white/80">Deals in Pipeline</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-amber-500 to-orange-600 text-white border-0">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-white/20">
                <TrendingUp className="w-5 h-5" />
              </div>
              <div>
                <p className="text-2xl font-bold">{formatCurrency(summary.total_approved_amount)}</p>
                <p className="text-sm text-white/80">Approved Value</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-purple-500 to-pink-600 text-white border-0">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-white/20">
                <DollarSign className="w-5 h-5" />
              </div>
              <div>
                <p className="text-2xl font-bold">{formatCurrency(summary.average_deal_size)}</p>
                <p className="text-sm text-white/80">Avg Deal Size</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Month Columns */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {months.map((month, idx) => (
          <Card key={month.month} className="overflow-hidden border-2 border-slate-200 shadow-lg">
            {/* Month Header */}
            <div className={`bg-gradient-to-r ${monthGradients[idx]} p-4 text-white`}>
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-2xl font-bold">{month.short_name}</h3>
                  <p className="text-white/80 text-sm">{month.month_name}</p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold">{formatCompactCurrency(month.total_amount)}</p>
                  <p className="text-white/80 text-sm">{month.deal_count} deals</p>
                </div>
              </div>
            </div>
            
            {/* Stats Bar */}
            <div className="grid grid-cols-2 divide-x divide-slate-200 bg-slate-50 border-b">
              <div className="p-3 text-center">
                <p className="text-xs text-slate-500 uppercase font-medium">Approved</p>
                <p className="text-lg font-bold text-emerald-600">{formatCompactCurrency(month.approved_amount)}</p>
                <p className="text-xs text-slate-500">{month.approved_count} deals</p>
              </div>
              <div className="p-3 text-center">
                <p className="text-xs text-slate-500 uppercase font-medium">Pending</p>
                <p className="text-lg font-bold text-blue-600">{formatCompactCurrency(month.total_amount - month.approved_amount)}</p>
                <p className="text-xs text-slate-500">{month.deal_count - month.approved_count} deals</p>
              </div>
            </div>
            
            {/* Deals List */}
            <div className="p-4 max-h-[400px] overflow-y-auto">
              {month.deals.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  <CalendarDays className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p className="font-medium">No projected deals</p>
                  <p className="text-sm">for this month</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {month.deals.map((deal) => {
                    const isSettled = deal.status_stage === 'settled';
                    return (
                    <div
                      key={deal.id}
                      onClick={() => !isSettled && openEditDate(deal)}
                      className={cn(
                        'bg-white border border-slate-200 rounded-lg p-3 transition-all group relative',
                        isSettled
                          ? 'opacity-80 border-emerald-200 bg-emerald-50/30'
                          : 'hover:shadow-lg hover:border-blue-300 cursor-pointer',
                      )}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1 min-w-0 pr-2">
                          <h4 className={cn(
                            'font-semibold truncate transition-colors',
                            isSettled ? 'text-emerald-700' : 'text-slate-900 group-hover:text-blue-600',
                          )}>{deal.client_name}</h4>
                          <p className="text-sm text-slate-500">{deal.broker}</p>
                        </div>
                        <div className="flex items-center gap-2 ml-2">
                          <p className="font-bold text-slate-900 text-right whitespace-nowrap">{formatCompactCurrency(deal.approved_amount || deal.est_loan_amount)}</p>
                          {/* Mark-settled tick */}
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); if (!isSettled) openMarkSettled(deal); }}
                            disabled={isSettled}
                            title={isSettled ? 'Settled' : 'Mark as settled'}
                            data-testid={`mark-settled-${deal.id}`}
                            className={cn(
                              'shrink-0 w-7 h-7 rounded-full flex items-center justify-center border transition-all',
                              isSettled
                                ? 'bg-emerald-500 border-emerald-600 text-white cursor-default'
                                : 'bg-white border-slate-300 text-slate-400 hover:border-emerald-500 hover:text-emerald-600 hover:bg-emerald-50 active:scale-95',
                            )}
                          >
                            {isSettled ? <CircleCheck className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>

                      <div className="flex items-center justify-between">
                        <span className={cn('text-xs px-2 py-1 rounded-full border font-medium', getStatusBadge(deal.status_stage))}>
                          {getStageLabel(deal.status_stage)}
                        </span>
                        <span className="text-xs text-slate-500">{deal.loan_type}</span>
                      </div>

                      <div className="mt-2 pt-2 border-t border-slate-100 flex items-center justify-between">
                        <div className="text-xs text-slate-500 flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {isSettled && deal.settled_date
                            ? `Settled ${new Date(deal.settled_date).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}`
                            : deal.projected_settlement_date
                              ? new Date(deal.projected_settlement_date).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })
                              : 'Auto-calculated'
                          }
                        </div>
                        {!isSettled && (
                          <span className="text-xs text-blue-500 opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
                            <Calendar className="w-3 h-3" /> Click to edit
                          </span>
                        )}
                      </div>
                    </div>
                  );})}
                </div>
              )}
            </div>
          </Card>
        ))}
      </div>

      {/* Edit Projected Date Dialog */}
      <Dialog open={!!editingDeal} onOpenChange={() => setEditingDeal(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Edit Projected Settlement Date</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-slate-600 mb-2">
              <strong>{editingDeal?.client_name}</strong> - {formatCurrency(editingDeal?.approved_amount || editingDeal?.est_loan_amount)}
            </p>
            <label className="text-sm font-medium mb-1 block">Projected Settlement Date</label>
            <Input 
              type="date" 
              value={editDate} 
              onChange={(e) => setEditDate(e.target.value)}
              data-testid="edit-projected-date"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingDeal(null)}>Cancel</Button>
            <Button onClick={handleUpdateProjectedDate}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Mark as Settled Dialog */}
      <Dialog open={!!settlingDeal} onOpenChange={(o) => { if (!o) setSettlingDeal(null); }}>
        <DialogContent className="max-w-sm" data-testid="mark-settled-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CircleCheck className="w-5 h-5 text-emerald-500" />
              Mark as Settled
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-3">
            <p className="text-sm text-slate-600">
              <strong className="text-slate-900">{settlingDeal?.client_name}</strong>
              {' '}— {formatCurrency(settlingDeal?.approved_amount || settlingDeal?.est_loan_amount)}
            </p>
            <div>
              <label className="text-sm font-medium mb-1.5 block text-slate-700">Settlement date</label>
              <Input
                type="date"
                value={settledDate}
                onChange={(e) => setSettledDate(e.target.value)}
                max={new Date().toISOString().slice(0, 10)}
                data-testid="settled-date-input"
                autoFocus
              />
              <p className="text-[11px] text-slate-500 mt-1.5">
                This deal will appear in this month&apos;s Settled figures on the dashboard.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSettlingDeal(null)} disabled={savingSettled}>
              Cancel
            </Button>
            <Button
              onClick={handleMarkSettled}
              disabled={savingSettled || !settledDate}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="settled-confirm-btn"
            >
              {savingSettled ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <CircleCheck className="w-4 h-4 mr-1.5" />}
              Confirm Settled
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
        </>
      )}
    </div>
  );
};


export { ProjectionsView };
export default ProjectionsView;
